import { useEffect, useRef } from "react";
import type { Citation } from "@/apis/cs-rag/types";
import { cn } from "@/lib/utils";
import { Maximize2, ZoomIn, ZoomOut, RotateCcw } from "lucide-react";
import { pushModal } from "@/stores/modal";
import { IconButton } from "@/toggle-ui-kit/icon-button";
import type { CitationIdMap } from "../hooks/use-parse-citations";
import { CitationCard, type CitationCardRef } from "./citation-card";
import { CitationViewerModal } from "@/app/help/chat/citation-viewer-modal";

interface CitationPanelProps {
  citations: Citation[];
  citationIdMap: CitationIdMap;
  selectedCitationNumber: number | null;
  highlightedId: string | null;
  onSelectCitation: (citationNumber: number) => void;
}

export const CitationPanel: React.FC<CitationPanelProps> = ({
  citations,
  citationIdMap,
  selectedCitationNumber,
  highlightedId,
  onSelectCitation,
}) => {
  const contentRef = useRef<HTMLDivElement>(null);
  const cardRef = useRef<CitationCardRef>(null);

  useEffect(() => {
    contentRef.current?.scrollTo({ top: 0 });
  }, [selectedCitationNumber]);

  if (citations.length === 0) {
    return (
      <div className="h-full flex items-center justify-center">
        <p className="body-2-normal-regular text-label-assistive">
          출처가 여기 표시됩니다
        </p>
      </div>
    );
  }

  const selectedCitation =
    citations.find((citation) => citation.n === selectedCitationNumber) ??
    citations[0];
  const selectedDomId = citationIdMap[selectedCitation.n];
  const availableCitations = citations.filter(
    (citation) => citationIdMap[citation.n],
  );
  const hasImages = (selectedCitation.images?.length ?? 0) > 0;

  return (
    <div className="h-full min-h-0 flex flex-col bg-background-neutral-neutral">
      <div className="shrink-0 border-b border-line-alternative bg-white px-3 py-2.5">
        {availableCitations.length > 1 && (
          <div className="mb-2 flex gap-1.5 overflow-x-auto pb-0.5">
            {availableCitations.map((citation) => {
              const domId = `citation-thumb-${citation.n}`;
              const isSelected = citation.n === selectedCitation.n;

              return (
                <button
                  key={citationIdMap[citation.n]}
                  id={domId}
                  type="button"
                  onClick={() => onSelectCitation(citation.n)}
                  className={cn(
                    "shrink-0 rounded-full border px-2.5 py-1 caption-1-semibold transition-colors",
                    isSelected || highlightedId === domId
                      ? "border-blue-600 bg-blue-600 text-white"
                      : "border-line-alternative bg-white text-label-alternative hover:bg-background-neutral-neutral",
                  )}
                >
                  {citation.n}
                  <span className="ml-1 opacity-72">p.{citation.page}</span>
                </button>
              );
            })}
          </div>
        )}

        <div className="flex items-center gap-2">
          <span className="shrink-0 min-w-[24px] px-2 py-0.5 rounded-full bg-blue-600 text-white caption-1-semibold text-center">
            {selectedCitation.n}
          </span>
          <div className="min-w-0 flex-1">
            <p className="label-1-normal-semibold text-label-normal truncate">
              출처 {selectedCitation.n}
            </p>
            <p className="caption-2-regular text-label-alternative">
              p.{selectedCitation.page}
            </p>
          </div>

          <div className="shrink-0 flex items-center gap-0.5">
            {hasImages && (
              <>
                <IconButton
                  icon={<ZoomIn className="size-3.5" />}
                  aria-label="확대"
                  title="확대"
                  onClick={() => cardRef.current?.zoomIn()}
                />
                <IconButton
                  icon={<ZoomOut className="size-3.5" />}
                  aria-label="축소"
                  title="축소"
                  onClick={() => cardRef.current?.zoomOut()}
                />
                <IconButton
                  icon={<RotateCcw className="size-3.5" />}
                  aria-label="맞춤"
                  title="맞춤"
                  onClick={() => cardRef.current?.resetTransform()}
                />
                <div className="w-px h-4 bg-line-alternative mx-0.5" />
              </>
            )}
            <IconButton
              icon={<Maximize2 className="size-3.5" />}
              aria-label="크게 보기"
              title="크게 보기"
              onClick={() =>
                pushModal(CitationViewerModal, {
                  citation: selectedCitation,
                })
              }
            />
          </div>
        </div>
      </div>

      <div ref={contentRef} className="flex-1 min-h-0 overflow-y-auto p-2.5">
        {selectedDomId ? (
          <CitationCard
            ref={cardRef}
            key={selectedDomId}
            citation={selectedCitation}
            domId={selectedDomId}
            isHighlighted={
              highlightedId === `citation-thumb-${selectedCitation.n}`
            }
          />
        ) : (
          <p className="text-center caption-1-regular text-label-assistive py-8">
            선택한 출처를 찾을 수 없습니다
          </p>
        )}
      </div>
    </div>
  );
};
